import { useMemo } from 'react';
import type { MouseEvent, VoidFunctionComponent } from 'react';
import type { NodeRendererProps } from 'react-arborist';

import { FileTreeNode } from '../../../generic/FileTree';
import { useNestingLines } from '../../../generic/FileTree/hooks';

import type { VendoredDependencyTreeNode } from './buildTree';

/**
 * Renders a single Node in the VendoredDependenciesTreeView. Gets its props from
 * `react-arborist` directly (see NodeRendererProps). This includes inline styles
 * which are applied directly to the element.
 */
const VendoredDependenciesTreeViewNode: VoidFunctionComponent<NodeRendererProps<VendoredDependencyTreeNode>> = ({
  node,
  style,
}) => {
  const isDirectory = node.data.type === 'directory';

  //  Handle clicking on the entire row.
  const handleSelectRow = (event: MouseEvent) => {
    node.handleClick(event);
  };

  const handleToggleDirectory = () => {
    //  "isInternal" is a react-arborist-ism for "this is a directory".
    //  Calling `node.toggle` will toggle the open/closed state of these nodes.
    if (node.isInternal) {
      node.toggle();
    }
  };

  // Generate nesting lines background for the current node level
  const nestingLinesBackgroundStyles = useNestingLines(node.level);
  const enhancedStyle = useMemo(() => ({
    ...style,
    ...nestingLinesBackgroundStyles,
  }), [nestingLinesBackgroundStyles, style]);

  return (
    <FileTreeNode
      style={enhancedStyle}
      onClick={handleSelectRow}
      isSelected={node.isSelected}
      isDirectory={isDirectory}
    >
      <FileTreeNode.Arrow
        isOpen={node.isOpen}
        isDirectory={isDirectory}
        onToggleDirectory={handleToggleDirectory}
      />
      {/*
        Technically all end nodes in vendored dependencies are directories, however the buildTree method
        doesn't account for this and would still count a final node as a "file".
      */}
      {!isDirectory && (<FileTreeNode.Icon isDirectory />)}
      <FileTreeNode.Text text={node.data.name} />
    </FileTreeNode>
  );
};

export default VendoredDependenciesTreeViewNode;
